<script setup lang="ts">
import { computed } from "vue";
import errorIconUrl from "@/assets/img/cross.png";
import validIconUrl from "@/assets/img/tick.png";

const model = defineModel<string>();

const props = defineProps<{
    id: string;
    placeholder?: string;
    type?: string;
    name?: string;
    tabindex?: number;
    disabled?: boolean;
    hasError?: boolean;
    validationClass?: string;
    fullWidth?: boolean;
    maxlength?: number;
    ariaLabel?: string;
}>();

const emit = defineEmits<{
    blur: [event: Event];
    change: [value: string];
}>();

function handleInput(event: Event) {
    const target = event.target as HTMLInputElement;
    emit("change", target.value);
}

const stateIcon = computed(() => {
    if (props.hasError) return { src: errorIconUrl, class: "error" };
    if (model.value && model.value !== "")
        return { src: validIconUrl, class: "valid" };
    return null;
});
</script>

<template>
    <div
        class="text-field"
        :class="{
            'text-field-error': props.hasError,
            'text-field-valid': model && model !== '',
            'text-field-fullwidth': props.fullWidth ?? false,
        }"
    >
        <input
            :id="props.id"
            v-model="model"
            :type="props.type || 'text'"
            :name="props.name"
            :tabindex="props.tabindex ?? 0"
            :placeholder="props.placeholder"
            :disabled="props.disabled"
            :aria-label="props.ariaLabel"
            class="text-field-input"
            :class="props.validationClass"
            :maxlength="props.maxlength"
            autocomplete="off"
            @blur="$emit('blur', $event)"
            @input="handleInput"
        />

        <img
            v-if="stateIcon"
            :src="stateIcon.src"
            :class="['text-field-icon', `text-field-icon-${stateIcon.class}`]"
            :alt="stateIcon.class"
        />
    </div>
</template>

<style scoped>
.text-field {
    position: relative;
    width: calc(((100% - (3 - 1) * 0.75rem) / 3) * 2 + 0.75rem);
    display: inline-block;
}

.text-field-input {
    width: 100%;
    height: 46px;
    padding: 0.875rem 1rem;
    border: 1px solid var(--grey-border);
    border-radius: 3px;
    background-color: var(--pure-white);
    font-size: var(--font-size-input);
    color: var(--off-black);
    box-sizing: border-box;
}

.text-field-fullwidth {
    width: 100%;
}

.text-field-input:focus {
    outline: none;
    border-color: var(--grey-dark);
    border-left-width: 3px;
    padding-left: calc(1rem - 2px) !important;
}

.text-field-icon {
    position: absolute;
    top: 50%;
    right: 0.6rem;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    pointer-events: none;
}

.text-field-valid .text-field-input {
    border-color: var(--color-primary-light) !important;
    background-color: var(--selection-background) !important;
}

.text-field-error .text-field-input {
    border-color: var(--error-border) !important;
    background-color: var(--error-background) !important;
}

input {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    color: var(--off-black);
}

@media screen and (width <= 584px) {
    .text-field {
        width: 100% !important;
    }
}
</style>
